import { create } from 'zustand';
import { config } from '../config';
import { AppNode, isGroupNode, isNodeInGroup } from '../types';
import { useNodeStore } from './nodeStore';
import { useGroupStore } from './groupStore';
import { CoordinateUtils, BoundingBox } from '../utils/coordinateUtils';
import { SpatialIndex } from '../utils/spatialIndex';
import { performanceMonitor } from '../utils/performanceMonitor';

const spatialIndex = new SpatialIndex();

/**
 * 刷新启动时的视口预水合（同步，模块加载阶段执行，早于任何组件渲染）：
 * 从 URL /project/:id 取项目 ID，同步读 localStorage 缓存里上次保存的视口，
 * 直接作为 canvasStore 初始值 → ReactFlow 挂载时 defaultViewport 即为上次视口，
 * 首帧就是原位置、原比例，消除"先 100% 再跳回"的闪烁。
 * （异步的项目数据加载随后到达时 restoreViewport 写入相同值，幂等无跳变）
 */
function readBootViewport(): { x: number; y: number; zoom: number } | null {
  try {
    const m = window.location.pathname.match(/^\/project\/([\w-]+)\/?$/);
    if (!m) return null;
    const tryKeys = [`bongai_project_cache_${m[1]}`, `bongai_canvas_${m[1]}`];
    for (const key of tryKeys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const vp = (JSON.parse(raw) as any)?.viewport;
      if (
        vp && Number.isFinite(vp.x) && Number.isFinite(vp.y) &&
        Number.isFinite(vp.zoom) && vp.zoom > 0
      ) {
        return { x: vp.x, y: vp.y, zoom: vp.zoom };
      }
    }
  } catch { /* 解析失败按默认视口启动 */ }
  return null;
}

const bootViewport = readBootViewport();
const clampZoom = (z: number) => Math.max(config.canvas.minZoom, Math.min(config.canvas.maxZoom, z));

interface CanvasState {
  x: number;
  y: number;
  zoom: number;
  edgeDeselectCounter: number;
  viewportSyncCounter: number;
  /** 启动时是否命中持久化视口（localStorage 预水合）；false 表示全新视口，FlowEditor 可安全应用小屏初始缩放 */
  hasStoredViewport: boolean;
  setViewport: (x: number, y: number, zoom: number) => void;
  restoreViewport: (viewport: { x: number; y: number; zoom: number } | null | undefined) => void;
  resetViewport: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  deselectEdges: () => void;

  checkNodeEnterGroup: (nodeId: string) => void;
  checkNodeExitGroup: (nodeId: string) => void;
  rebuildSpatialIndex: () => void;
  findNodesInBounds: (bounds: BoundingBox) => string[];
}

export const useCanvasStore = create<CanvasState>((set, get) => ({
  x: bootViewport?.x ?? 0,
  y: bootViewport?.y ?? 0,
  zoom: clampZoom(bootViewport?.zoom ?? config.canvas.defaultZoom),
  edgeDeselectCounter: 0,
  viewportSyncCounter: 0,
  hasStoredViewport: bootViewport !== null,

  setViewport: (x, y, zoom) => set({
    x,
    y,
    zoom: clampZoom(zoom)
  }),

  // 刷新/加载项目后按持久化数据原样还原视口（位置与缩放比例不变，不做 fitView）
  // bump viewportSyncCounter 通知已挂载的 FlowEditor 将 store 视口应用到 ReactFlow 实例
  // （defaultViewport 仅在 ReactFlow 挂载瞬间生效，挂载后的变更必须走实例 setViewport）
  restoreViewport: (viewport) => {
    if (!viewport) return;
    const { x, y, zoom } = viewport;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(zoom) || zoom <= 0) return;
    set({
      x,
      y,
      zoom: clampZoom(zoom),
      viewportSyncCounter: get().viewportSyncCounter + 1,
    });
  },

  resetViewport: () => set({ x: 0, y: 0, zoom: config.canvas.defaultZoom }),

  zoomIn: () => {
    const { zoom } = get();
    const newZoom = Math.min(config.canvas.maxZoom, zoom * 1.2);
    set({ zoom: newZoom });
  },

  zoomOut: () => {
    const { zoom } = get();
    const newZoom = Math.max(config.canvas.minZoom, zoom / 1.2);
    set({ zoom: newZoom });
  },

  deselectEdges: () => set((s) => ({ edgeDeselectCounter: s.edgeDeselectCounter + 1 })),

  checkNodeEnterGroup: (nodeId: string) => {
    const nodeState = useNodeStore.getState();
    const nodes = nodeState.nodes;
    const nodeMap = nodeState.nodeMap;
    const node = nodeMap.get(nodeId);
    if (!node || isGroupNode(node) || isNodeInGroup(node)) return;

    const nodeCenter = CoordinateUtils.getNodeCenter(node);

    const groupIds = useGroupStore.getState().getAllGroupIds();
    for (const gid of groupIds) {
      const group = nodeMap.get(gid);
      if (!group || !isGroupNode(group)) continue;

      const groupConfig = useGroupStore.getState().getGroupById(gid);
      if (groupConfig?.childIds.includes(nodeId)) continue;

      const bounds: BoundingBox = {
        x: group.x,
        y: group.y,
        width: group.width || 400,
        height: group.height || 300,
      };

      if (CoordinateUtils.isPointInBounds(nodeCenter, bounds)) {
        useNodeStore.getState()._batchUpdateNodes([
          {
            id: nodeId,
            changes: {
              parentNode: gid,
              groupId: gid,
            },
          },
        ]);

        useGroupStore.getState()._addChildToGroup(gid, nodeId);
        break;
      }
    }
  },

  checkNodeExitGroup: (nodeId: string) => {
    const nodeState = useNodeStore.getState();
    const nodeMap = nodeState.nodeMap;
    const node = nodeMap.get(nodeId);
    if (!node || !node.groupId) return;

    const groupId = node.groupId;
    const group = nodeMap.get(groupId);
    if (!group) return;

    const bounds: BoundingBox = {
      x: group.x,
      y: group.y,
      width: group.width || 400,
      height: group.height || 300,
    };

    const nodeCenter = CoordinateUtils.getNodeCenter(node);

    if (!CoordinateUtils.isPointInBounds(nodeCenter, bounds, 30)) {
      useNodeStore.getState()._batchUpdateNodes([
        {
          id: nodeId,
          changes: {
            parentNode: undefined,
            extent: undefined,
            expandParent: undefined,
            groupId: undefined,
          },
        },
      ]);

      useGroupStore.getState()._removeChildFromGroup(groupId, nodeId);
    }
  },

  rebuildSpatialIndex: () => {
    performanceMonitor.start('rebuildSpatialIndex');
    const nodes = useNodeStore.getState().nodes;
    spatialIndex.clear();
    spatialIndex.build(
      nodes
        .filter(n => !isGroupNode(n))
        .map(n => ({
          id: n.id,
          bounds: CoordinateUtils.getNodeBounds(n),
        }))
    );
    performanceMonitor.end('rebuildSpatialIndex');
  },

  findNodesInBounds: (bounds: BoundingBox) => {
    const results = spatialIndex.query(bounds);
    return results.map(r => r.id);
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}
